'use client';

import { Fragment, Suspense } from 'react';
import { TapLink } from './tap-link';
import { AwaitNumber } from './await-value';
import { Skeleton } from './ui';
import { useT } from '@/lib/locale-context';
import styles from './dock.module.css';

/**
 * The compact `dock` affordance (docs/MOBILE.md §2.3-2.4): a floating pill in
 * the thumb zone. The default row starts with Back (`back`, a lone glass
 * circle in the seat of the item page's Back, 2026-10-20); the cluster after
 * it holds the constant nav homes (four at most; nav-page links, so opening
 * one clears the "how I got here" trail - plain URLs, per the accumulation
 * rule); the right end carries the ONE primary action of the current page
 * (`action` - resolved by the shell: a page-registered act via `DockAction`,
 * else the shell's default create). Self-contained and decoupled from
 * routing: the shell decides what mounts here and when it shows (CSS-gated to
 * compact viewports; per the rendering rule this component is always in the
 * DOM).
 *
 * `children` append non-link nav affordances (e.g. the quick-switcher
 * trigger) styled the same as the link items.
 */
export interface DockLink {
  href: string;
  label: string;
  icon: React.ReactNode;
  active: boolean;
  /**
   * Streamed unread count; >0 shows a small pill over the icon. Only the pill
   * suspends (await-value.tsx), never the dock itself - a streamed fallback of
   * a structural container can transiently duplicate the landmark.
   */
  unread?: Promise<number>;
}

export function Dock({
  items,
  back,
  action,
  navClaimed = false,
  navOverride,
  children,
}: {
  items: DockLink[];
  /** The default row's first slot (docs/MOBILE.md §2.4): Back as a lone
   *  circle before the cluster. A page that claims the nav region draws its
   *  own row, so this is not rendered there. */
  back?: DockPodButton;
  /** The page-action region's content; null/undefined renders no region. */
  action?: React.ReactNode;
  /** A page claimed the nav region (docs/MOBILE.md §2.4, the contextual row):
   *  `navOverride` renders IN PLACE of the nav links + `children`. The action
   *  region is untouched by the override. */
  navClaimed?: boolean;
  navOverride?: React.ReactNode;
  children?: React.ReactNode;
}) {
  const t = useT();
  return (
    <nav className={styles.dock} aria-label={t('shell.nav.primary')} data-testid="dock">
      {navClaimed ? (
        // Contextual mode owns the whole row shape (pods + springs); the
        // cluster pill is default-nav chrome only.
        navOverride
      ) : (
        <>
          {back && (
            <span className={styles.podCircle}>
              <PodButton item={back} />
            </span>
          )}
          <div className={styles.cluster}>
            {items.map((it) => (
              <TapLink
                key={it.href}
                href={it.href}
                className={styles.item}
                data-active={it.active || undefined}
                aria-current={it.active ? 'page' : undefined}
              >
                <span className={styles.icon}>
                  {it.icon}
                  {it.unread && (
                    <Suspense fallback={null}>
                      <AwaitNumber value={it.unread}>
                        {(n) =>
                          n > 0 ? (
                            <span
                              // eslint-disable-next-line no-restricted-syntax -- an unread counter on an icon's corner, not a chip (docs/DESIGN_SYSTEM.md "Chip"; the CountBadge follow-up in the board item "The one chip: what the wave left open")
                              className={styles.badge}
                              aria-label={t('shell.nav.unread', { count: n })}
                            >
                              {n > 99 ? '99+' : n}
                            </span>
                          ) : null
                        }
                      </AwaitNumber>
                    </Suspense>
                  )}
                </span>
                <span className={styles.label}>{it.label}</span>
              </TapLink>
            ))}
            {children}
          </div>
        </>
      )}
      {action != null && <div className={styles.action}>{action}</div>}
    </nav>
  );
}

/** A non-link dock affordance (e.g. the quick-switcher trigger, the item
 *  page's Back / "…" row), styled like the items. */
export function DockButton({
  label,
  icon,
  onClick,
  testId,
}: {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  testId?: string;
}) {
  return (
    <button type="button" className={styles.item} onClick={onClick} data-testid={testId}>
      <span className={styles.icon}>{icon}</span>
      <span className={styles.label}>{label}</span>
    </button>
  );
}

/**
 * The declarative contextual row (2026-07-18): pods from a DEFINITION instead
 * of hand-assembled JSX. Each side lists items; the component owns the
 * shapes - one non-accent item renders as a lone glass circle, several group
 * into one glass pill, an `accent: true` item renders as the highlighted
 * word segment inside its group, and a `render` item is the caller-drawn
 * split act in the same accent voice (see `DockPodItem.render`). Contextual pods are
 * icon-only otherwise (the label is the accessible name; captions are
 * default-nav vocabulary); every target is thumb-sized by the module's pod
 * metrics.
 */
export interface DockPodItem {
  key: string;
  label: string;
  onClick?: () => void;
  /** The icon face (ignored when `accent` - the accent segment speaks the
   *  label word instead). */
  icon?: React.ReactNode;
  /** The highlighted act of its group: an accent word segment. */
  accent?: boolean;
  /**
   * A SPLIT ACT segment (the item page's next step; accent since 2026-09-16):
   * the slot is an accent pill, and the caller draws the two segments inside
   * it - the word, which fires the step, and a caret, which opens the rest.
   * It is pushed, not parked: the dock's action slot is where a page's
   * primary act lives in accent, so the next step wears the same voice
   * `accent` items do, and the 2026-09-15 quiet word past a hairline seam is
   * retired (an accent pill needs no seam). The caller owns the control
   * because it fires its own act, and wears the class handed to it for the
   * word segment. Rendering nothing takes the slot with it.
   */
  render?: (className: string) => React.ReactNode;
  /** The split act's QUIET register (Leon, 2026-11-03): the slot draws the
   *  outline in place of the accent fill, for a word that is the way back
   *  (Reopen on a done or cancelled item). */
  quiet?: boolean;
  /**
   * The ICON pod, drawn by the caller (the item page's "…", 2026-09-16): same
   * face as `icon`, wearing the class handed to it, and no seam - it sits with
   * the icons rather than past them. The slot exists because the "…" opens its
   * own menu, so the pod button must BE that menu's trigger.
   */
  renderIcon?: (className: string) => React.ReactNode;
  /**
   * The split act's SHAPE while the surface loads (docs/LOADING.md §4.2): a
   * pill-shaped `Skeleton` of this width in the seat the `render` item's pill
   * takes once the page lands, so the row does not move at the handover. It
   * is no control: nothing to tap, nothing announced.
   */
  shape?: string;
  /** An icon pod that is present but takes no tap yet (the item skeleton's
   *  "…", whose rows need the item). */
  disabled?: boolean;
  testId?: string;
}

/** The fields of a plain icon pod: what `PodButton` draws. */
export type DockPodButton = Pick<DockPodItem, 'label' | 'onClick' | 'icon' | 'disabled' | 'testId'>;

/** A plain icon pod: icon-only, the label its accessible name. */
function PodButton({ item }: { item: DockPodButton }) {
  return (
    <button
      type="button"
      className={styles.podItem}
      aria-label={item.label}
      title={item.label}
      disabled={item.disabled || undefined}
      onClick={() => item.onClick?.()}
      data-testid={item.testId}
    >
      {item.icon}
    </button>
  );
}

export function DockPods({ left, right }: { left: DockPodItem[]; right: DockPodItem[] }) {
  const pod = (items: DockPodItem[]) => {
    if (items.length === 0) return null;
    const lone = items.length === 1 && !items[0]!.accent && !items[0]!.render && !items[0]!.shape;
    return (
      <span className={lone ? styles.podCircle : styles.podGroup}>
        {items.map((it) =>
          it.render ? (
            <span key={it.key} className={styles.podWordSlot} data-quiet={it.quiet || undefined}>
              {it.render(styles.podWord ?? '')}
            </span>
          ) : it.shape ? (
            <span key={it.key} className={styles.podShapeSlot} data-testid={it.testId}>
              <Skeleton shape="block" width={it.shape} height="44px" className={styles.podShape} />
            </span>
          ) : it.renderIcon ? (
            <Fragment key={it.key}>{it.renderIcon(styles.podItem ?? '')}</Fragment>
          ) : it.accent ? (
            <button
              key={it.key}
              type="button"
              className={styles.podAccent}
              onClick={() => it.onClick?.()}
              data-testid={it.testId}
            >
              {it.label}
            </button>
          ) : (
            <PodButton key={it.key} item={it} />
          ),
        )}
      </span>
    );
  };
  return (
    <>
      {pod(left)}
      <span className={styles.spring} aria-hidden />
      {pod(right)}
    </>
  );
}

/**
 * The dock's page-action control: the accent-filled round act at the pill's
 * right end (the FAB's successor - one shape shared by the shell's default
 * create and page-registered acts, so the slot swap is invisible).
 */
export function DockActionButton({
  label,
  onClick,
  testId,
  wide = false,
  children,
}: {
  label: string;
  onClick: () => void;
  testId?: string;
  /** A word-carrying act (the item page's "Review" mirror): pill-shaped
   *  instead of the round icon circle. */
  wide?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={styles.actionBtn}
      data-wide={wide || undefined}
      aria-label={label}
      title={label}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
